// src/pages/Gallery.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";

/**
 * Hardcoded public gallery
 * - Images must exist at: /public/galerie/<filename>
 * - Update FILES below with your exact .jpg names (case sensitive).
 */

// base public path (change if you use a basePath)
const BASE = "/galerie";

// ⬇️ EDIT THIS LIST to match your files exactly
const FILES = [
  // Examples based on your screenshot; replace/add as needed:
  "IMG_0438.jpg",
  "IMG_0439.jpg",
  "IMG_0440.jpg",
  "IMG_0443.jpg",
  "IMG_0444.jpg",
  "IMG_0454.jpg",
  "IMG_0455.jpg",
  "IMG_0456.jpg",
  "IMG_0459.jpg",
  "IMG_0460.jpg",
  "IMG_0922.jpg",
  "IMG_0923.jpg",
  "IMG_0924.jpg",
  "IMG_0925.jpg",
  "IMG_1067.jpg",
  "IMG_1068.jpg",
];

function ThumbImg({ src, altText, className = "" }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div className="w-full h-full grid place-items-center text-xs text-gray-500">
      nu s-a putut încărca
    </div>
  ) : (
    <img
      src={src}
      alt={altText}
      className={className}
      loading="lazy"
      decoding="async"
      draggable="false"
      onError={() => setFailed(true)}
    />
  );
}

function Lightbox({ items, index, onClose, onPrev, onNext, alts = [] }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    let startX = 0,
      startY = 0;
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx > 0 ? onPrev() : onNext();
      }
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onPrev, onNext]);

  if (index < 0 || index >= items.length) return null;
  const { url } = items[index];
  const alt = alts[index] || "Manu Pro Detailing – fotografie mare";

  return (
    <div ref={containerRef} className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Închide"
          className="rounded-full bg-white/20 hover:bg-white/30 text-white px-4 py-2"
        >
          ✕
        </button>
      </div>

      {loading && (
        <div className="pointer-events-none fixed inset-0 z-[60] grid place-items-center">
          <div className="rounded-full bg-black/40 p-3">
            <svg
              className="h-6 w-6 animate-spin text-white"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-20"
              />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-80"
              />
            </svg>
          </div>
        </div>
      )}

      <div className="absolute inset-0 grid place-items-center p-4">
        <figure className="relative max-h-[90vh] max-w-[95vw]">
          <img
            src={url}
            alt={alt}
            className={
              "max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-lg transition-opacity duration-200 " +
              (loading ? "opacity-0" : "opacity-100")
            }
            loading="eager"
            decoding="async"
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />

          <button
            onClick={onPrev}
            aria-label="Anterior"
            disabled={loading}
            className={
              "absolute inset-y-0 left-0 w-1/3 flex items-center justify-start px-2 focus:outline-none " +
              (loading ? "pointer-events-none" : "")
            }
            style={{ background: "transparent" }}
          >
            {!loading && (
              <span className="text-white/70 text-4xl leading-none select-none">
                ‹
              </span>
            )}
          </button>
          <button
            onClick={onNext}
            aria-label="Următor"
            disabled={loading}
            className={
              "absolute inset-y-0 right-0 w-1/3 flex items-center justify-end px-2 focus:outline-none " +
              (loading ? "pointer-events-none" : "")
            }
            style={{ background: "transparent" }}
          >
            {!loading && (
              <span className="text-white/70 text-4xl leading-none select-none">
                ›
              </span>
            )}
          </button>
        </figure>
      </div>
    </div>
  );
}

export default function Gallery({ setPage }) {
  useEffect(() => {
    document.title = "Galerie – Manu Pro Detailing";
  }, []);

  const pageTitle = "Galerie";
  const loadingText = "Se încarcă galeria…";
  const backHome = "Înapoi la Acasă";

  // build items from FILES
  const items = useMemo(
    () => FILES.map((name) => ({ name, url: `${BASE}/${name}` })),
    []
  );

  const alts = useMemo(
    () => items.map((_, i) => `Manu Pro Detailing – foto ${i + 1}`),
    [items]
  );

  const [lbIndex, setLbIndex] = useState(-1);

  const openAt = useCallback((i) => {
    setLbIndex(i);
    if (typeof window !== "undefined") {
      window.history.pushState({ lightbox: true, i }, "", `#photo-${i + 1}`);
    }
  }, []);

  const prev = useCallback(
    () => setLbIndex((i) => (i > 0 ? i - 1 : Math.max(0, items.length - 1))),
    [items.length]
  );
  const next = useCallback(
    () => setLbIndex((i) => (i + 1) % Math.max(1, items.length)),
    [items.length]
  );
  const closeLb = useCallback(() => {
    setLbIndex(-1);
    if (
      typeof window !== "undefined" &&
      window.location.hash.startsWith("#photo-")
    ) {
      const clean = window.location.href.replace(/#photo-\d+$/, "");
      window.history.replaceState({}, "", clean);
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      if (typeof window === "undefined") return;
      if (window.location.hash.startsWith("#photo-")) {
        const n = parseInt(window.location.hash.replace("#photo-", ""), 10);
        if (!isNaN(n) && n >= 1 && n <= items.length) {
          setLbIndex(n - 1);
          return;
        }
      }
      setLbIndex(-1);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", onPop);
      onPop();
      return () => window.removeEventListener("popstate", onPop);
    }
  }, [items.length]);

  const hasGrid = items.length > 0;

  return (
    <main>
      <section id="gallery" className="max-w-6xl mx-auto px-4 pt-10 pb-12">
        {/* Back */}
        <div className="mb-5">
          <button
            onClick={() => {
              if (setPage) {
                setPage("home");
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.history.back();
              }
            }}
            className="
              group inline-flex items-center gap-2
              rounded-full px-5 py-2.5
              bg-manupro-primary text-white
              shadow-brand ring-1 ring-manupro-ring/40
              hover:bg-manupro-teal active:translate-y-px
              transition focus:outline-none focus-visible:ring-2 focus-visible:ring-manupro-primary/60
            "
            aria-label={backHome}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-5 w-5 -ml-1 transition-transform group-hover:-translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span className="font-medium">{backHome}</span>
          </button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-2 text-manupro-ink">
          {pageTitle}
        </h1>

        {/* Content */}
        {!hasGrid ? (
          <div className="text-sm text-manupro-ink/80">
            Nu există imagini configurate. Adaugă numele fișierelor în
            <code> FILES </code> din <code>Gallery.jsx</code>.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {items.map((it, i) => (
                <div
                  key={it.url}
                  className="aspect-square rounded-2xl overflow-hidden border bg-white"
                >
                  <button
                    onClick={() => openAt(i)}
                    className="w-full h-full"
                    title="Deschide imaginea"
                  >
                    <ThumbImg
                      src={it.url}
                      altText={alts[i]}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.03]"
                    />
                  </button>
                </div>
              ))}
            </div>

            {lbIndex >= 0 && (
              <Lightbox
                items={items}
                index={lbIndex}
                onClose={closeLb}
                onPrev={prev}
                onNext={next}
                alts={alts}
              />
            )}
          </>
        )}
      </section>
    </main>
  );
}
